/**
 * Logger Type Definitions
 *
 * Single pipeline: call site -> LogEntry -> redaction -> LogTransport[].
 * Field names are snake_case so a future remote sink can ship entries as-is.
 */

/**
 * Available log levels in order of severity
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

/**
 * Predefined logging categories for different app domains
 */
export enum LogCategory {
  AUTH = 'auth',
  API = 'api',
  UI = 'ui',
  NETWORK = 'network',
  STORAGE = 'storage',
  DATABASE = 'database',
}

/**
 * Architectural layer that produced the entry (mirrors the Go `layer` field).
 */
export enum LogLayer {
  FRONTEND = 'frontend',
  ORCHESTRATOR = 'orchestrator',
}

/**
 * Configuration options for the logger
 */
export interface LoggerConfig {
  /**
   * Minimum log level to output (inclusive). Applies to every transport.
   * @default LogLevel.DEBUG
   */
  minLevel?: LogLevel;

  /**
   * Categories to enable logging for. If empty, all categories are enabled.
   * Applies to every transport.
   * @default []
   */
  categories?: LogCategory[];

  /**
   * Whether the console transport writes (production stays silent unless
   * this or the debug flag in app config is enabled).
   * @default true in development, false in production
   */
  enableConsole?: boolean;
}

/**
 * Context object that can be passed with log messages
 * Can contain any data, but sensitive fields will be redacted
 */
export interface LogContext {
  [key: string]: any;
}

/**
 * Serialized error carried by error entries. `stack` is always present.
 */
export interface LogError {
  name: string;
  message: string;
  stack: string;
}

/**
 * Structured log entry handed to every transport.
 * Context and error are already redacted by the pipeline.
 */
export interface LogEntry {
  level: LogLevel;
  category?: LogCategory;
  message: string;
  /** Epoch milliseconds */
  ts: number;
  /** Client-side correlation id for one action execution */
  request_id?: string;
  layer?: LogLayer;
  error?: LogError;
  context?: LogContext;
}

/**
 * A destination for log entries. Entries arrive filtered and redacted.
 * `write` may throw or reject: the pipeline isolates it from the caller.
 */
export interface LogTransport {
  name: string;
  write(entry: LogEntry): void | Promise<void>;
}

/**
 * Logger instance interface for a specific category
 */
export interface CategoryLogger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: Error | unknown, context?: LogContext): void;
}

/**
 * Logger bound to a request id (and optionally a layer). Every line it
 * emits, including via its category loggers, carries them.
 */
export interface BoundLogger extends CategoryLogger {
  auth: CategoryLogger;
  api: CategoryLogger;
  ui: CategoryLogger;
  network: CategoryLogger;
  storage: CategoryLogger;
  database: CategoryLogger;
}

/**
 * Main logger interface
 */
export interface Logger extends BoundLogger {
  /**
   * Child logger whose entries all carry `request_id` (and `layer`).
   */
  withRequest(requestId: string, layer?: LogLayer): BoundLogger;

  /**
   * Configure logger behavior at runtime
   */
  configure(config: LoggerConfig): void;
}

/**
 * Keys that contain sensitive data to be redacted
 */
export const SENSITIVE_KEYS = [
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'authorization',
  'sessionId',
  'deviceId',
  'deviceFingerprint',
] as const;

/**
 * Log level severity values for comparison
 */
export const LOG_LEVEL_SEVERITY: Record<LogLevel, number> = {
  [LogLevel.DEBUG]: 0,
  [LogLevel.INFO]: 1,
  [LogLevel.WARN]: 2,
  [LogLevel.ERROR]: 3,
};

/**
 * Value declared sensitive at its definition site (frontend analogue of the
 * Go `-- @sensitive` annotation). Redacted by the pipeline whatever the key
 * name, and it serializes as `[REDACTED]` even if it escapes the logger.
 * Use `markSensitive(value)` to create one and `.reveal()` to read it.
 */
export class Sensitive<T = unknown> {
  readonly #value: T;

  constructor(value: T) {
    this.#value = value;
  }

  reveal(): T {
    return this.#value;
  }

  toJSON(): string {
    return REDACTED;
  }

  toString(): string {
    return REDACTED;
  }
}

export const REDACTED = '[REDACTED]';

/**
 * Declare a value sensitive.
 *
 * @example
 * const input = { email, password: markSensitive(password) };
 */
export function markSensitive<T>(value: T): Sensitive<T> {
  return new Sensitive(value);
}
